import React, { useCallback, useEffect, useRef, useState } from "react";
import { Form, Modal, notification, Progress, TableProps } from "antd";
import axios from "axios";
import useSWR from 'swr';
import { useAuth } from "../context/AuthContext";
import { debounce } from 'lodash';

interface DataType {
    key: React.Key;
    name: string;
    age: number;
    address: string;
}

interface AddItemAvailableDataType {
    key: React.Key;
    item_code: string;
    item_name: string;
    demand_qty: number;
    available_qty: number;
    status: string;
    uom: string;
    location_code_rack: string;
    warehouse?: string;
    supply_qty?: number;
}

interface SalesMasterRecord {
    key: React.Key;
    id: number;
    so_no: string;
    sales_date: string;
    cs_name: string;
    close_order_reason?: string;
    user_approved?: string;
    sales_person?: string;
    status?: string;
    se_no?: string;
    trans_type?: string;
    order_type?: string;
    cs_code?: string;
    purchase_order_no?: string;
    top?: string;
    agreement_no?: string;
    credit_limit_approval_status?: string;
    sub_total?: number | string;
    item_disc?: number | string;
    total_a?: number | string;
    vat?: number | string;
    grand_total?: number | string;
    delivery_address1?: string;
    delivery_address2?: string;
    contact_person?: string;
    contact_phone?: string;
    deliver_via?: string;
    warehouse_group?: string;
    approval_remark?: string;
    outstanding_remark?: string;
    remark?: string;
    credit_limit_remaining?: number | string;
}

const backendUrl = process.env.REACT_APP_BACKEND_URL;

interface SalesOrderTypeRecord {
    id: number;
    code: string;
    deskripsi: string;
}

interface DataWarehouseRecord {
    id: number;
    warehouse_name: string;
    warehouse_code: string;
}

interface SalesTransactionTypeRecord {
    id: number;
    code: string;
    deskripsi: string;
}

export interface SalesOrderItem {
    key: React.Key;
    item_code: string;
    item_name: string;
    uom: string;
    location_code_rack: string;
    available_quantity: number;
    demand_quantity: number;
    supply_quantity: number;
    pick_quantity?: number | string;
    return_qty?: string;
    so_no?: string;
    warehouse?: string;
}

interface PriorityOrderItem {
    qty: number;
    so_no: string;
}

interface PickingStatus {
    item_code: string;
    total_picked: number;
    total_demand: number;
}

export const statusMapRecord: Record<string, string> = {
    "1": "Draft",
    "7": "Ready",
    "4": "Close / Complete",
    "6": "Closed",
    "99": "Return",
};

export const getDisplayStatus = (status: string | undefined): string => {
    if (!status) return "";
    return statusMapRecord[status] || status;
};

const fetcher = (url: string) => fetch(url).then((res) => {
    if (!res.ok) {
        throw new Error('Failed to fetch data');
    }
    return res.json();
});

const AvailableQuantityCell: React.FC<{ itemCode: string; warehouse: string; location: string; initialQty: number }> = ({ itemCode, warehouse, location, initialQty }) => {
    const { data } = useSWR(
        itemCode && warehouse && location
            ? `${backendUrl}/api/spareparts-stocks/qty?item_code=${encodeURIComponent(itemCode)}&warehouse=${encodeURIComponent(warehouse)}&location_code_rack=${encodeURIComponent(location)}`
            : null,
        fetcher
    );

    const displayQty = data && data.qty !== undefined ? data.qty : initialQty;
    return <span>{displayQty}</span>;
};

const columns = (selectedWarehouseGroupCode?: string) => {
    return [
        { title: "Item Code", dataIndex: "item_code", key: "item_code", onCell: () => ({ style: { whiteSpace: 'nowrap' } }) },
        { title: "Item Name", dataIndex: "item_name", key: "item_name", onCell: () => ({ style: { whiteSpace: 'nowrap' } }) },
        { title: "UOM", dataIndex: "uom", key: "uom", onCell: () => ({ style: { whiteSpace: 'nowrap' } }) },
        { title: "Location", dataIndex: "location_code_rack", key: "location_code_rack", onCell: () => ({ style: { whiteSpace: 'nowrap' } }) },
        {
            title: "Available Quantity",
            dataIndex: "available_quantity",
            key: "available_quantity",
            onCell: () => ({ style: { whiteSpace: 'nowrap' } }),
            render: (text: any, record: SalesOrderItem) => (
                <AvailableQuantityCell
                    itemCode={record.item_code}
                    warehouse={selectedWarehouseGroupCode || ''}
                    location={record.location_code_rack}
                    initialQty={Number(text || 0)}
                />
            )
        },
        { title: "Demand Quantity", dataIndex: "demand_quantity", key: "demand_quantity", onCell: () => ({ style: { whiteSpace: 'nowrap' } }) },
        { title: "Supply Quantity", dataIndex: "supply_quantity", key: "supply_quantity", onCell: () => ({ style: { whiteSpace: 'nowrap' } }) },
        { title: "Pick Quantity", dataIndex: "pick_quantity", key: "pick_quantity", onCell: () => ({ style: { whiteSpace: 'nowrap' } }) },
        { title: "Return Qty", dataIndex: "return_qty", key: "return_qty", onCell: () => ({ style: { whiteSpace: 'nowrap' } }) },
    ];
}

const usePickingListLogic = () => {
    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
    const [pickingListItems, setPickingListItems] = useState<SalesOrderItem[]>([]);
    const [form] = Form.useForm<any>();
    const { userData } = useAuth();
    const rowSelection: TableProps<DataType>["rowSelection"] = {
        selectedRowKeys,
        onChange: (newSelectedRowKeys) => setSelectedRowKeys(newSelectedRowKeys),
    };

    const [isSoModalVisible, setIsSoModalVisible] = useState(false);
    const [isAddItemModalVisible, setIsAddItemModalVisible] = useState(false);
    const [isAddEditPickingItemFormModalVisible, setIsAddEditPickingItemFormModalVisible] = useState(false);
    const [selectedOrderTypeCode, setSelectedOrderTypeCode] = useState<string | undefined>(undefined);
    const [selectedWarehouseGroupCode, setSelectedWarehouseGroupCode] = useState<string | undefined>(undefined);
    const [selectedTransactionTypeCode, setSelectedTransactionTypeCode] = useState<string | undefined>(undefined);
    const [selectedSalesOrderNo, setSelectedSalesOrderNo] = useState<string | undefined>(undefined);
    const [isEditMode, setIsEditMode] = useState(false);
    const initialPickingNoRef = useRef<string | null>(null);
    const isFirstRender = useRef(true);
    const [isReadyStatus, setIsReadyStatus] = useState(false);
    const [editingPickingItem, setEditingPickingItem] = useState<SalesOrderItem | null>(null);
    const [isStatusDraft, setIsStatusDraft] = useState(true);
    const [autosaveStatus, setAutosaveStatus] = useState<'idle' | 'processing' | 'error' | 'stopped'>('idle');
    const [isPrinting, setIsPrinting] = useState(false);
    const [isVoiding, setIsVoiding] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [pickingStatusMap, setPickingStatusMap] = useState<Map<string, { picked: number; demand: number }>>(new Map());
    const [creatorKodeGrup1, setCreatorKodeGrup1] = useState<string | null>(null);

    const { data: dataWarehouseData, error: dataWarehouseError } = useSWR<DataWarehouseRecord[]>(
        `${backendUrl}/api/picking-data-warehouse`,
        fetcher
    );

    const { data: salesOrderTypesData, error: salesOrderTypesError } = useSWR<SalesOrderTypeRecord[]>(
        `${backendUrl}/api/sales-order-types`,
        fetcher
    );

    const { data: salesTransactionTypesData, error: salesTransactionTypesError } = useSWR<SalesTransactionTypeRecord[]>(
        `${backendUrl}/api/sales-transaction-types`,
        fetcher
    );

    const getFormattedDateForPickingNo = (date: Date): string => {
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = String(date.getFullYear()).substring(2);
        return `${month}${year}`;
    };
    const fetchLatestPickingListNo = async (datePart: string): Promise<string | null> => {
        try {
            const response = await axios.get(`${backendUrl}/api/picking-list/latest-no?datePart=${datePart}`);
            return response.data.latestNo;
        } catch (error) {
            console.error("Gagal mengambil nomor Picking List terbaru:", error);
            notification.error({ message: "Gagal mengambil nomor Picking List terbaru dari server." });
            return null;
        }
    };

    const calculateNextPickingListNo = (latestNo: string | null, datePart: string): string => {
        let nextSequence = 1;
        if (latestNo && latestNo.startsWith(`SPPL-AMP${datePart}`)) {
            const existingSequenceStr = latestNo.substring(latestNo.length - 5);
            const existingSequence = parseInt(existingSequenceStr, 10);
            if (!isNaN(existingSequence)) {
                nextSequence = existingSequence + 1;
            }
        }
        const formattedSequence = String(nextSequence).padStart(5, '0');
        return `SPPL-AMP${datePart}${formattedSequence}`;
    };
    const resetToNewForm = useCallback(async () => {
        setIsEditMode(false);
        form.resetFields();
        setPickingListItems([]);
        setIsReadyStatus(false);
        setEditingPickingItem(null);
        const today = new Date();
        const formattedDate = `${String(today.getDate()).padStart(2, '0')}/${String(today.getMonth() + 1).padStart(2, '0')}/${today.getFullYear()}`;
        form.setFieldsValue({ date_picking: formattedDate, status: "Draft", created_user: userData?.nama });
        setIsStatusDraft(true);
        isInitialLoad.current = false;
        setPickingStatusMap(new Map());
        setCreatorKodeGrup1(null);
        setSelectedSalesOrderNo(undefined);
        setSelectedWarehouseGroupCode(undefined);
        setSelectedOrderTypeCode(undefined);
        setSelectedTransactionTypeCode(undefined);
        setSelectedRowKeys([]);
    }, [form, userData]);

    const isInitialLoad = useRef(true);
    useEffect(() => {

        if (isFirstRender.current) {
            isFirstRender.current = false;
            const pickingListToEdit = sessionStorage.getItem('pickingListToEdit');
            if (pickingListToEdit) {
                sessionStorage.removeItem('pickingListToEdit');
                setIsEditMode(true);
                isInitialLoad.current = true;
                fetchPickingListDetails(pickingListToEdit);
            } else {
                resetToNewForm();
            }
        }

    }, [form, userData, resetToNewForm]);


    useEffect(() => {
        if (isEditMode && form.getFieldValue('no_picking')) {
            isInitialLoad.current = false;
        }
    }, [isEditMode, form.getFieldValue('no_picking')]);

    const fetchPickingStatusBySoNo = async (soNo: string) => {
        if (!soNo) {
            setPickingStatusMap(new Map());
            return new Map();
        }
        try {
            const response = await axios.get<PickingStatus[]>(`${backendUrl}/api/picking-list/picking-status-by-so?soNo=${soNo}`);
            const newMap = new Map<string, { picked: number; demand: number }>();
            response.data.forEach(item => {
                newMap.set(item.item_code, { picked: Number(item.total_picked), demand: Number(item.total_demand) });
            });
            setPickingStatusMap(newMap);
            return newMap;
        } catch (error) {
            console.error("Failed to fetch picking status by SO:", error);
            notification.error({ message: "Gagal mengambil status picking dari database untuk SO ini." });
            setPickingStatusMap(new Map());
            return new Map();
        }
    };

    const fetchPickingListDetails = async (noPicking: string) => {
        try {
            const response = await axios.get(`${backendUrl}/api/picking-list/details/${noPicking}`);
            const { header, items } = response.data;

            if (header) {

                const formattedSalesDate = header.sales_order_date ? (() => {
                    const parts = header.sales_order_date.split('/');
                    if (parts.length === 3) {
                        const day = parseInt(parts[0], 10);
                        const month = parseInt(parts[1], 10);
                        const year = parseInt(parts[2], 10);
                        const dateObject = new Date(year, month - 1, day);
                        if (!isNaN(dateObject.getTime())) {
                            return dateObject.toLocaleDateString('en-GB');
                        }
                    }
                    return '';
                })() : '';


                let orderTypeDisplay = header.order_type;
                if (salesOrderTypesData && header.order_type) {
                    const foundOrderType = salesOrderTypesData.find(ot => ot.code === header.order_type);
                    if (foundOrderType) {
                        orderTypeDisplay = foundOrderType.deskripsi;
                    }
                }

                const warehouseGroupDisplay = getWarehouseNameFromCode(header.warehouse_group, dataWarehouseData);

                let transactionTypeDisplay = header.transaction_type;
                if (salesTransactionTypesData && header.transaction_type) {
                    const foundTransactionType = salesTransactionTypesData.find(tt => tt.code === header.transaction_type);
                    if (foundTransactionType) {
                        transactionTypeDisplay = foundTransactionType.deskripsi;
                    }
                }

                const displayStatus = getDisplayStatus(header.status);

                form.setFieldsValue({
                    status: displayStatus,
                    no_picking: header.no_picking,
                    date_picking: header.date_picking,
                    sales_order: header.sales_order,
                    sales_order_date: formattedSalesDate,
                    transaction_type: transactionTypeDisplay,
                    customer_name: header.customer_name,
                    customer_code: header.cs_code,
                    purchase_order: header.purchase_order,
                    order_type: orderTypeDisplay,
                    warehouse_group: warehouseGroupDisplay,
                    created_user: header.created_user_name,
                    set_order: header.set_order === "1",
                    back_order: header.back_order === "1",
                    remark: header.remark,
                });
                setCreatorKodeGrup1(header.created_user);
                setIsReadyStatus(displayStatus === "Ready");
                setIsStatusDraft(displayStatus === "Draft");
                setSelectedOrderTypeCode(header.order_type || '');
                setSelectedWarehouseGroupCode(header.warehouse_group || '');
                setSelectedTransactionTypeCode(header.transaction_type || '');
                setSelectedSalesOrderNo(header.sales_order);
                await fetchPickingStatusBySoNo(header.sales_order);
            }
            if (items) {
                const itemsWithKeys: SalesOrderItem[] = await Promise.all(items.map(async (item: any, index: number) => {

                    let totalPickedForThisItemFromOtherLists = 0;
                    if (header.sales_order && item.item_code) {
                        try {

                            const response = await axios.get(`${backendUrl}/api/picking-list/total-picked-qty`, {
                                params: {
                                    itemCode: item.item_code,
                                    salesOrderNo: header.sales_order,
                                    currentPickingNo: header.no_picking
                                }
                            });
                            totalPickedForThisItemFromOtherLists = response.data.totalPickedQty;
                        } catch (error) {
                            console.error("Gagal mengambil total kuantitas yang sudah di-pick untuk item saat memuat:", item.item_code, error);
                            totalPickedForThisItemFromOtherLists = 0;
                        }
                    }

                    const currentItemPickQty = parseFloat(String(item.pick_qty || 0));
                    const calculatedSupplyQty = totalPickedForThisItemFromOtherLists + currentItemPickQty;

                    return {
                        ...item,
                        key: item.item_code + '-' + index,
                        available_quantity: parseFloat(item.available_qty),
                        demand_quantity: parseFloat(item.demand_qty),
                        supply_quantity: parseFloat(item.supply_qty || '0'),
                        pick_quantity: item.pick_qty,
                        return_qty: item.return_qty,
                        so_no: header.sales_order,
                    };
                }));
                setPickingListItems(itemsWithKeys);
            }
            notification.success({ message: "Picking list berhasil dimuat." });
        } catch (error) {
            console.error("Gagal memuat detail picking list:", error);
            notification.error({ message: "Gagal memuat detail picking list dari server." });
            setIsEditMode(false);
            form.resetFields();
            setPickingListItems([]);
            setIsReadyStatus(false);
            setIsStatusDraft(true);
            const today = new Date();
            const formattedDate = `${String(today.getDate()).padStart(2, '0')}/${String(today.getMonth() + 1).padStart(2, '0')}/${today.getFullYear()}`;
            form.setFieldsValue({ date_picking: formattedDate, status: "Draft", created_user: userData?.kode_grup1 });
            initialPickingNoRef.current = null;
            setSelectedSalesOrderNo(undefined);
            setPickingStatusMap(new Map());
        } finally {
            isInitialLoad.current = false;
        }
    };

    const getWarehouseNameFromCode = (code: string | undefined, data: DataWarehouseRecord[] | undefined): string | undefined => {
        if (!code || !data) {
            return code;
        }
        const foundWarehouse = data.find(wh => wh.warehouse_code === code);
        return foundWarehouse ? foundWarehouse.warehouse_name : code;
    };

    const showSoModal = () => {
        setIsSoModalVisible(true);
    };

    const handleSoModalOk = () => {
        setIsSoModalVisible(false);
    };

    const handleSoModalCancel = () => {
        setIsSoModalVisible(false);
    };
    const showAddItemModal = () => {
        setIsAddItemModalVisible(true);
        const currentSoNo = form.getFieldValue('sales_order');
        setSelectedSalesOrderNo(currentSoNo);
    };

    const handleAddItemModalOk = () => {
        setIsAddItemModalVisible(false);
    };

    const handleAddItemModalCancel = () => {
        setIsAddItemModalVisible(false);
    };

    const showAddEditPickingItemFormModal = () => {
        setIsAddEditPickingItemFormModalVisible(true);
    };

    const handleAddEditPickingItemFormModalOk = async (updatedItem?: SalesOrderItem) => {
        if (updatedItem) {

            const currentSalesOrderNo = form.getFieldValue('sales_order');
            const originalSupplyQty = parseFloat(String(editingPickingItem?.supply_quantity || 0));
            const originalPickQty = parseFloat(String(editingPickingItem?.pick_quantity || 0));
            const currentItemPickQty = parseFloat(String(updatedItem.pick_quantity || 0));
            const newSupplyQuantity = (originalSupplyQty - originalPickQty) + currentItemPickQty;

            setPickingListItems(prevItems => {
                const newItems = prevItems.map(item =>
                    item.key === updatedItem.key ? { ...updatedItem, supply_quantity: newSupplyQuantity, so_no: currentSalesOrderNo } : item
                );
                debouncedAutoSave({}, form.getFieldsValue(), newItems);
                return newItems;
            });

            notification.success({ message: "Item berhasil diperbarui." });
        }
        setIsAddEditPickingItemFormModalVisible(false);
        setEditingPickingItem(null);
    };

    const handleAddEditPickingItemFormModalCancel = () => {
        setIsAddEditPickingItemFormModalVisible(false);
        setEditingPickingItem(null);
    };

    const handleRowDoubleClick = (record: SalesOrderItem) => {
        setEditingPickingItem(record);
        showAddEditPickingItemFormModal();
    };

    const fetchSalesOrderItems = async (soNo: string, warehouseGroup: string): Promise<SalesOrderItem[]> => {
        try {
            const response = await axios.get(`${backendUrl}/api/sales-order-items?soNo=${soNo}&warehouseGroup=${warehouseGroup}`);
            const groupedItems: { [key: string]: any[] } = {};
            response.data.forEach((item: any) => {
                if (!groupedItems[item.item_code]) {
                    groupedItems[item.item_code] = [];
                }
                groupedItems[item.item_code].push(item);
            });
            const finalData: any[] = [];
            Object.keys(groupedItems).forEach(itemCode => {
                const items = groupedItems[itemCode];
                const demandQty = Number(items[0].demand_quantity || 0);
                items.sort((a, b) => Number(b.available_quantity || 0) - Number(a.available_quantity || 0));
                    let remainingDemand = demandQty;
                    items.forEach(item => {
                        if (remainingDemand > 0) {
                            finalData.push(item);
                            remainingDemand -= Number(item.available_quantity || 0);
                        }
                    });
            });
            const itemsWithKeys: SalesOrderItem[] = finalData.map((item: any, index: number) => ({
                ...item,
                key: item.item_code + '-' + index,
                pick_quantity: '',
                so_no: soNo,
            }));
            return itemsWithKeys;
        } catch (error) {
            console.error("Failed to fetch sales order items:", error);
            notification.error({ message: "Gagal mengambil detail item Sales Order." });
            return [];
        }
    };

    const fetchPriorityOrderItem = async (itemCode: string): Promise<PriorityOrderItem | null> => {
        try {
            const response = await axios.get(`${backendUrl}/api/priority-order-item?itemCode=${itemCode}`);
            if (response.data) {
                return {
                    qty: parseInt(response.data.qty, 10),
                    so_no: response.data.so_no
                };
            }
            return null;
        } catch (error) {
            console.error("Gagal mengambil item prioritas:", error);

            return null;
        }
    };

    const updateFormFields = (record: SalesMasterRecord) => {
        const formattedSalesDate = record.sales_date ? (() => {
            const parts = record.sales_date.split('/');
            if (parts.length === 3) {
                const day = parseInt(parts[0], 10);
                const month = parseInt(parts[1], 10);
                const year = parseInt(parts[2], 10);
                const dateObject = new Date(year, month - 1, day);
                if (!isNaN(dateObject.getTime())) {
                    return dateObject.toLocaleDateString('en-GB');
                }
            }
            return '';
        })() : '';
        let orderTypeDisplay = record.order_type;
        if (salesOrderTypesData && record.order_type) {
            const foundOrderType = salesOrderTypesData.find(ot => ot.code === record.order_type);
            if (foundOrderType) {
                orderTypeDisplay = foundOrderType.deskripsi;
            }
        }

        const warehouseGroupDisplay = getWarehouseNameFromCode(record.warehouse_group, dataWarehouseData);

        let transactionTypeDisplay = record.trans_type;
        if (salesTransactionTypesData && record.trans_type) {
            const foundTransactionType = salesTransactionTypesData.find(tt => tt.code === record.trans_type);
            if (foundTransactionType) {
                transactionTypeDisplay = foundTransactionType.deskripsi;
            }
        }

        form.setFieldsValue({
            sales_order: record.so_no,
            sales_order_date: formattedSalesDate,
            customer_name: record.cs_name,
            customer_code: record.cs_code,
            purchase_order: record.purchase_order_no,
            order_type: orderTypeDisplay,
            warehouse_group: warehouseGroupDisplay,
            transaction_type: transactionTypeDisplay
        });
        setSelectedOrderTypeCode(record.order_type || '');
        setSelectedWarehouseGroupCode(record.warehouse_group || '');
        setSelectedTransactionTypeCode(record.trans_type || '');
    };

    const handleSoRecordSelected = async (record: SalesMasterRecord) => {
        setIsSoModalVisible(false);
        setAutosaveStatus('processing');
        try {
            const today = new Date();
            const formattedDateDDMMYY = getFormattedDateForPickingNo(today);
            const latestExistingNo = await fetchLatestPickingListNo(formattedDateDDMMYY);
            const newPickingNo = calculateNextPickingListNo(latestExistingNo, formattedDateDDMMYY);
            form.setFieldsValue({ no_picking: newPickingNo });
            initialPickingNoRef.current = newPickingNo;
            const allItems = await fetchSalesOrderItems(record.so_no, record.warehouse_group || '');
            const processedItems: SalesOrderItem[] = [];
            const itemsWithNegativeQtyReason: { itemCode: string; reason: string }[] = [];
            const itemsAlreadySupplied: { itemCode: string; itemName: string; demandQty: number; pickedQty: number }[] = [];
            for (const item of allItems) {

                const currentDemandQty = parseFloat(String(item.demand_quantity || 0));
                const currentSupplyQty = parseFloat(String(item.supply_quantity || 0));
                if (currentDemandQty > 0 && currentSupplyQty >= currentDemandQty) {
                    itemsAlreadySupplied.push({
                        itemCode: item.item_code,
                        itemName: item.item_name,
                        demandQty: currentDemandQty,
                        pickedQty: currentSupplyQty
                    });
                    continue;
                }
                let finalAvailableQuantity = item.available_quantity;
                let reasonForNegativeOrZero = '';
                const priorityData = await fetchPriorityOrderItem(item.item_code);
                if (priorityData) {
                    const priorityQty = priorityData.qty;
                    const isSoMatch = priorityData.so_no === record.so_no;
                    const potentialAvailableQty = item.available_quantity - priorityQty;

                    finalAvailableQuantity = potentialAvailableQty;

                    if (isSoMatch) {
                        reasonForNegativeOrZero = `kuantitas tidak cukup setelah prioritas untuk Sales Order yang cocok.`;
                    } else {
                        reasonForNegativeOrZero = `kuantitas tidak cukup setelah prioritas untuk Sales Order yang tidak cocok.`;
                    }
                } else if (item.available_quantity <= 0) {
                    reasonForNegativeOrZero = `kuantitas tersedia awal nol atau negatif.`;
                }
                if (finalAvailableQuantity <= 0) {
                    itemsWithNegativeQtyReason.push({
                        itemCode: item.item_code,
                        reason: reasonForNegativeOrZero || `kuantitas tersedia nol atau negatif.`,
                    });
                } else {
                    processedItems.push({
                        ...item,
                        available_quantity: item.available_quantity,
                        pick_quantity: '',
                        so_no: record.so_no,
                        supply_quantity: currentSupplyQty,
                        warehouse: item.warehouse,
                    });
                }
            }
            if (itemsWithNegativeQtyReason.length > 0 || itemsAlreadySupplied.length > 0) {
                const modalParagraphs: React.ReactNode[] = [];
                if (itemsAlreadySupplied.length > 0) {
                    modalParagraphs.push(<p key="supplied-header" style={{ fontWeight: 'bold', color: 'red' }}>Beberapa item sudah disuplai sepenuhnya:</p>);
                    itemsAlreadySupplied.forEach((item, index) => {
                        modalParagraphs.push(
                            <p key={`supplied-item-${index}`} style={{ marginBottom: '0.5em', color: 'red' }}>
                                - Item {item.itemCode} ({item.itemName}): Demand {item.demandQty}, Sudah di-pick {item.pickedQty}. <span style={{ fontWeight: 'bold' }}>"Already supplied!"</span>
                            </p>
                        );
                    });
                    modalParagraphs.push(<br key="br1" />);
                }

                if (itemsWithNegativeQtyReason.length > 0) {
                    modalParagraphs.push(<p key="negative-header">Beberapa item memiliki kuantitas tersedia nol:</p>);
                    itemsWithNegativeQtyReason.forEach((entry, index) => {
                        const originalItem = allItems.find(ai => ai.item_code === entry.itemCode);
                        modalParagraphs.push(
                            <p key={`item-${index}-reason`} style={{ marginBottom: '0.5em' }}>
                                - Item {entry.itemCode} ({originalItem?.item_name || 'N/A'}): Kuantitas tersedia {originalItem?.available_quantity}.
                            </p>
                            // <p key={`item-${index}-reason`} style={{ marginBottom: '0.5em' }}>
                            //     - Item {entry.itemCode} ({originalItem?.item_name || 'N/A'}): Kuantitas tersedia {originalItem?.available_quantity}. Alasan: {entry.reason}
                            // </p>
                        );
                    });
                    modalParagraphs.push(<br key="br2" />);
                }

                if (processedItems.length > 0) {
                    modalParagraphs.push(<p key="available-header" style={{ fontWeight: 'bold', color: 'green', marginTop: '1em' }}>Item yang akan ditambahkan ke daftar picking:</p>);
                    processedItems.forEach((item, index) => {
                        modalParagraphs.push(
                            <p key={`available-item-${index}`} style={{ marginBottom: '0.5em', color: 'green' }}>
                                - Item {item.item_code} ({item.item_name}): Tersedia {item.available_quantity}, Permintaan {item.demand_quantity}. <span style={{ fontWeight: 'bold' }}>"Available for picking!"</span>
                            </p>
                        );
                    });
                    modalParagraphs.push(<br key="br3" />);
                }

                modalParagraphs.push(<p key="question" style={{ marginTop: '1em' }}>Apakah Anda ingin melanjutkan picking item yang tersedia saja?</p>);
                const contentNode = (
                    <div>
                        {modalParagraphs}
                    </div>
                );
                Modal.confirm({
                    title: 'KONFIRMASI Kuantitas Item',
                    content: contentNode,
                    okText: 'YA',
                    cancelText: 'TIDAK',
                    className: 'modal-confirm-item',
                    onOk: () => {
                        setPickingListItems(processedItems);
                        updateFormFields(record);
                        setSelectedSalesOrderNo(record.so_no);
                        autoSaveFormData(
                            {},
                            {
                                ...form.getFieldsValue(),
                                order_type_code_for_save: record.order_type || '',
                                warehouse_group_code_for_save: record.warehouse_group || '',
                                transaction_type_code_for_save: record.trans_type || ''
                            },
                            processedItems
                        );
                    },
                    onCancel: () => {
                        setPickingListItems([]);
                        setSelectedSalesOrderNo(undefined);
                        form.setFieldsValue({
                            sales_order: undefined,
                            sales_order_date: undefined,
                            customer_name: undefined,
                            customer_code: undefined,
                            purchase_order: undefined,
                            order_type: undefined,
                            warehouse_group: undefined,
                            transaction_type: undefined
                        });
                        setAutosaveStatus('idle');
                        setPickingStatusMap(new Map());
                    },
                });

            } else {

                setPickingListItems(processedItems);
                updateFormFields(record);
                setSelectedSalesOrderNo(record.so_no);
                autoSaveFormData(
                    {},
                    {
                        ...form.getFieldsValue(),
                        order_type_code_for_save: record.order_type || '',
                        warehouse_group_code_for_save: record.warehouse_group || '',
                        transaction_type_code_for_save: record.trans_type || ''
                    },
                    processedItems
                );
            }
        } catch (error) {
            console.error("Failed to process sales order selection:", error);
            notification.error({ message: "Gagal memproses pemilihan Sales Order." });
            setPickingListItems([]);
            setSelectedSalesOrderNo(undefined);
            setAutosaveStatus('error');
            setPickingStatusMap(new Map());
        }
    };

    const handleDeleteSelectedItems = () => {
        if (selectedRowKeys.length === 0) {
            notification.warning({ message: "Pilih setidaknya satu item untuk dihapus." });
            return;
        }

        Modal.confirm({
            title: 'Konfirmasi Hapus Item',
            content: 'Apakah Anda yakin ingin menghapus item yang dipilih?',
            okText: 'Ya',
            cancelText: 'Tidak',
            onOk: () => {
                const updatedItems = pickingListItems.filter(item => !selectedRowKeys.includes(item.key));
                setPickingListItems(updatedItems);
                setSelectedRowKeys([]);
                debouncedAutoSave({}, form.getFieldsValue(), updatedItems);
            },
        });
    };

    const handleSelectAvailableItems = (selectedItems: AddItemAvailableDataType[]) => {
        const newItems: SalesOrderItem[] = selectedItems.map((item, index) => ({
            key: item.item_code + '-' + Date.now() + '-' + index,
            item_code: item.item_code,
            item_name: item.item_name,
            demand_quantity: item.demand_qty,
            available_quantity: item.available_qty,
            uom: item.uom,
            location_code_rack: item.location_code_rack,
            supply_quantity: Number(item.supply_qty || 0),
            pick_quantity: '',
            so_no: form.getFieldValue('sales_order'),
            warehouse: (item as any).warehouse || selectedWarehouseGroupCode,
        }));

        setPickingListItems(prevItems => {
            const updatedItems = [...prevItems, ...newItems];
            debouncedAutoSave({}, form.getFieldsValue(), updatedItems);
            return updatedItems;
        });
    };

    const autoSaveFormData = useCallback(async (changedValues: any, allValues: any, itemsOverride?: SalesOrderItem[]) => {

        if (isInitialLoad.current) {
            return;
        }

        if (!isEditMode) {
            if (autosaveStatus !== 'processing') {
                setAutosaveStatus('processing');
            }
        } else if (Object.keys(changedValues).length > 0 || itemsOverride) {
            setAutosaveStatus('processing');
        }

        if (isReadyStatus) {
            console.log("Auto-save dinonaktifkan: Status adalah Ready.");
            setAutosaveStatus('stopped');
            return;
        }

        const currentFormValues = form.getFieldsValue();
        if (!isEditMode && currentFormValues.no_picking === initialPickingNoRef.current && Object.keys(changedValues).length === 0 && !itemsOverride) {
            setAutosaveStatus('idle');
            return;
        }

        if (!currentFormValues.no_picking) {
            console.warn("Tidak dapat auto-save: Nomor Picking List belum tersedia.");
            setAutosaveStatus('error');
            return;
        }

        if (!currentFormValues.sales_order && !isEditMode) {
            console.warn("Tidak dapat auto-save: Sales Order No belum diisi. Auto-save dibatalkan.");
            setAutosaveStatus('idle');
            return;
        }

        try {
            const itemsToUse = itemsOverride || pickingListItems;
            const dataToAutoSave = {
                status: currentFormValues.status === "Draft" ? "1" : currentFormValues.status,
                no_picking: currentFormValues.no_picking,
                date_picking: currentFormValues.date_picking,
                sales_order: currentFormValues.sales_order,
                sales_order_date: currentFormValues.sales_order_date,
                transaction_type: allValues.transaction_type_code_for_save !== undefined ? allValues.transaction_type_code_for_save : selectedTransactionTypeCode,
                customer_name: currentFormValues.customer_name,
                customer_code: currentFormValues.customer_code,
                purchase_order: currentFormValues.purchase_order || '',
                order_type: allValues.order_type_code_for_save !== undefined ? allValues.order_type_code_for_save : selectedOrderTypeCode,
                warehouse_group: allValues.warehouse_group_code_for_save !== undefined ? allValues.warehouse_group_code_for_save : selectedWarehouseGroupCode,
                created_user: isEditMode ? creatorKodeGrup1 : userData?.kode_grup1,
                set_order: currentFormValues.set_order ? "1" : "0",
                back_order: currentFormValues.back_order ? "1" : "0",
                picking_list_items: itemsToUse.map(item => ({
                    no_picking: currentFormValues.no_picking,
                    item_code: item.item_code,
                    item_name: item.item_name,
                    uom: item.uom,
                    location_rack: item.location_code_rack,
                    available_qty: String(item.available_quantity),
                    demand_qty: String(item.demand_quantity),
                    supply_qty: String(item.pick_quantity || ''),
                    pick_qty: String(item.pick_quantity || ''),
                    return_qty: String(item.return_qty || ''),
                    so_no: currentFormValues.sales_order,
                    warehouse: item.warehouse,
                })),
            };

            console.log("Auto-saving data:", dataToAutoSave);

            const backendUrl = process.env.REACT_APP_BACKEND_URL;
            if (!backendUrl) {
                notification.error({ message: "Backend URL tidak dikonfigurasi." });
                setAutosaveStatus('error');
                return;
            }
            await axios.post(`${backendUrl}/api/picking-list`, dataToAutoSave);
            setAutosaveStatus('idle');

        } catch (error: any) {
            console.error("Gagal menyimpan otomatis picking list:", error);
            notification.error({ message: "Gagal menyimpan otomatis picking list." });
            setAutosaveStatus('error');

        }
    }, [form, selectedTransactionTypeCode, selectedOrderTypeCode, selectedWarehouseGroupCode, pickingListItems, isEditMode, initialPickingNoRef, isReadyStatus, autosaveStatus]);


    const debouncedAutoSave = useCallback(
        debounce((changedValues: any, allValues: any, itemsOverride?: SalesOrderItem[]) => {
            autoSaveFormData(changedValues, allValues, itemsOverride);
        }, 1000),
        [autoSaveFormData]
    );

    if (salesOrderTypesError) {
        console.error("Failed to load sales order types:", salesOrderTypesError);
    }

    if (dataWarehouseError) {
        console.error("Failed to load data warehouse:", dataWarehouseError);
    }

    if (salesTransactionTypesError) {
        console.error("Failed to load sales transaction types:", salesTransactionTypesError);
    }

    const handleVoid = () => {
        const currentPickingNo = form.getFieldValue('no_picking');
        if (!currentPickingNo) {
            notification.warning({ message: "Tidak ada Nomor Picking List untuk dibatalkan." });
            return;
        }

        Modal.confirm({
            title: 'Konfirmasi Pembatalan Picking List',
            content: `Apakah Anda yakin ingin membatalkan Picking List Nomor: ${currentPickingNo}? Tindakan ini akan menghapus data terkait secara permanen.`,
            okText: 'Ya, Batalkan',
            cancelText: 'Tidak',
            okButtonProps: { danger: true },
            onOk: async () => {
                setIsVoiding(true);
                try {
                    await axios.delete(`${backendUrl}/api/picking-list/${currentPickingNo}`);
                    notification.success({ message: `Picking List ${currentPickingNo} berhasil dibatalkan.` });
                    resetToNewForm();
                } catch (error) {
                    console.error("Gagal membatalkan picking list:", error);
                    notification.error({ message: "Gagal membatalkan picking list dari server." });
                } finally {
                    setIsVoiding(false);
                }
            },
        });
    };

    const handleSubmit = async () => {

        const currentPickingNo = form.getFieldValue('no_picking');
        const salesOrderNo = form.getFieldValue('sales_order');

        setIsSubmitting(true);
        if (!currentPickingNo || !salesOrderNo) {
            notification.warning({ message: "Nomor Picking List atau Sales Order tidak ditemukan." });
            setIsSubmitting(false);
            return;
        }

        const itemsWithoutPickQty = pickingListItems.filter(item => !item.pick_quantity || parseFloat(String(item.pick_quantity)) <= 0);
        if (itemsWithoutPickQty.length > 0) {
            notification.error({ message: "Submit Gagal", description: "Semua item harus memiliki 'Pick Quantity' yang lebih besar dari 0 untuk di-submit." });
            setIsSubmitting(false);
            return;
        }

        try {
            const stockChecks = await Promise.all(
                pickingListItems.map(async (item) => {
                    const response = await axios.get(`${backendUrl}/api/spareparts-stocks/qty`, {
                        params: {
                            item_code: item.item_code,
                            warehouse: item.warehouse || selectedWarehouseGroupCode || '',
                            location_code_rack: item.location_code_rack,
                        }
                    });
                    const dbQty = Number(response.data?.qty || 0);
                    const pickQty = parseFloat(String(item.pick_quantity || 0));
                    return {
                        item_code: item.item_code,
                        insufficient: dbQty < pickQty,
                    };
                })
            );
            const hasInsufficient = stockChecks.some(check => check.insufficient);
            if (hasInsufficient) {
                notification.error({
                    message: "Supply tidak cukup harap cek stock parts"
                });
                setIsSubmitting(false);
                return;
            }
        } catch (err) {
            console.error("Gagal memeriksa stok spareparts:", err);
            notification.error({ message: "Gagal memeriksa ketersediaan stok." });
            setIsSubmitting(false);
            return;
        }

        Modal.confirm({
            title: 'Konfirmasi Submit Picking List',
            content: `Anda akan men-submit Picking List ${currentPickingNo} untuk Sales Order ${salesOrderNo}. Lanjutkan?`,
            okText: 'Ya, Submit',
            cancelText: 'Tidak',
            onCancel: () => setIsSubmitting(false),
            onOk: async () => {
                try {
                    await axios.put(`${backendUrl}/api/picking-list/submit`, {
                        no_picking: currentPickingNo,
                        sales_order_no: salesOrderNo,
                        picking_list_items: pickingListItems.map(item => ({
                            item_code: item.item_code,
                            pick_qty: item.pick_quantity,
                            warehouse: item.warehouse,
                            location_code_rack: item.location_code_rack,
                        })),
                    });
                    form.setFieldsValue({ status: 'Ready' });
                    setIsReadyStatus(true)
                    setIsStatusDraft(false);
                    notification.success({ message: `Picking List ${currentPickingNo} berhasil disubmit.` });
                } catch (error: any) {
                    console.error("Gagal submit picking list:", error);
                    const errorMessage = error.response?.data?.message || "Gagal submit picking list ke server.";
                    notification.error({ message: "Submit Gagal", description: errorMessage });
                } finally {
                    setIsSubmitting(false);
                }
            },
        });

    };

    const handlePrint = async () => {
        const noPicking = form.getFieldValue('no_picking');
        if (!noPicking) {
            notification.warning({ message: "Nomor Picking List tidak ditemukan untuk dicetak." });
            return;
        }
        setIsPrinting(true);
        const key = 'print_progress_notification';
        const updateProgress = (percent: number) => {
            notification.info({
                key,
                message: 'Mempersiapkan Dokumen',
                description: <Progress percent={percent} status="active" />,
                duration: 0,
                placement: 'topRight',
            });
        };
        try {
            updateProgress(0);
            await axios.post(`${backendUrl}/api/picking-list/log-print`, { no_picking: noPicking });
            const reportUrl = `${process.env.PUBLIC_URL}/reports/picking_list/picking_list.html`;
            const htmlResponse = await axios.get(reportUrl, { responseType: 'text' });
            let htmlContent = htmlResponse.data;
            const bootstrapCssResponse = await axios.get('/reports/picking_list/bootstrap/css/bootstrap.css', { responseType: 'text' });
            const customCssResponse = await axios.get('/reports/picking_list/style.css', { responseType: 'text' });
            const bootstrapCssContent = bootstrapCssResponse.data;
            const customCssContent = customCssResponse.data;
            const noBorderCss = `
                .table tbody tr td,
                .table tbody tr th {
                    border: none !important;
                }
            `;
            htmlContent = htmlContent.replace(/<link[^>]*href="bootstrap\/css\/bootstrap\.css"[^>]*>/, '');
            htmlContent = htmlContent.replace(/<link[^>]*href="style\.css"[^>]*>/, '');
            htmlContent = htmlContent.replace(
                '</head>',
                `<style>${bootstrapCssContent}\n${customCssContent}\n${noBorderCss}</style>\n</head>`
            );
            const dataResponse = await axios.get(`${backendUrl}/api/picking-list/print-data/${noPicking}`);
            const { header, items, duplicateCount } = dataResponse.data;
            updateProgress(20);
            const duration = 4000;
            const interval = 100;
            const steps = duration / interval;
            const increment = (100 - 20) / steps;
            let currentPercent = 20;
            for (let i = 0; i < steps; i++) {
                await new Promise(resolve => setTimeout(resolve, interval));
                currentPercent += increment;
                updateProgress(Math.round(currentPercent));
            }
            notification.destroy(key);
            if (!header) {
                notification.error({ message: "Data header Picking List tidak ditemukan." });
                return;
            }

            if (duplicateCount > 1) {
                const watermarkStyle = `
                    <style>
                    .watermark {
                        position: fixed;
                        top: 50%;
                        left: 50%;
                        transform: translate(-50%, -50%) rotate(-45deg);
                        color: black;
                        opacity: 0.1;
                        font-size: 100px;
                        font-weight: bold;
                        pointer-events: none;
                        z-index: 1000;
                        text-align: center;
                        width: 100%;
                    }
                    </style>
                    `;
                const watermarkDiv = `<div class="watermark">Duplicate (${duplicateCount})</div>`;
                htmlContent = htmlContent.replace('</head>', `${watermarkStyle}</head>`);
                htmlContent = htmlContent.replace('</body>', `${watermarkDiv}</body>`);
            }

            const today = new Date();
            const docDate = today.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
            const warehouseName = getWarehouseNameFromCode(header.warehouse_group, dataWarehouseData);
            htmlContent = htmlContent.replace('cs_name', header.customer_name || '');
            htmlContent = htmlContent.replace('cs_code', header.cs_code || '');
            htmlContent = htmlContent.replace('cs_po', header.purchase_order || '');
            htmlContent = htmlContent.replace('order_date', header.sales_order_date || '');
            htmlContent = htmlContent.replace('sales_order_no', header.sales_order || '');
            htmlContent = htmlContent.replace('doc_no', header.no_picking || '');
            htmlContent = htmlContent.replace('doc_date', docDate);
            htmlContent = htmlContent.replace('warehouse', warehouseName || '');
            htmlContent = htmlContent.replace('sampit_date_placeholder', docDate);
            let tableRowsHtml = '';
            let totalDemandQty = 0;
            let totalSupplyQty = 0;
            const tbodyStartIndex = htmlContent.indexOf('<tbody>');
            const tbodyEndIndex = htmlContent.indexOf('</tbody>') + '</tbody>'.length;
            const originalTbodyContent = htmlContent.substring(tbodyStartIndex, tbodyEndIndex);
            items.forEach((item: any, index: number) => {
                tableRowsHtml += `
                    <tr>
                        <th scope="row" style="padding-top: 1px; padding-bottom: 1px;">${index + 1}</th>
                        <td style="padding-top: 1px; padding-bottom: 1px;">${item.item_code || ''}</td>
                        <td style="padding-top: 1px; padding-bottom: 1px;">${item.item_name || ''}</td>
                        <td style="padding-top: 1px; padding-bottom: 1px;">${item.location_rack || ''}</td>
                        <td style="padding-top: 1px; padding-bottom: 1px;">${item.demand_qty || 0}</td>
                        <td style="padding-top: 1px; padding-bottom: 1px;">${item.pick_qty || 0}</td>
                    </tr>
                `;
                totalDemandQty += parseFloat(item.demand_qty || 0);
                totalSupplyQty += parseFloat(item.pick_qty || 0);
            });
            htmlContent = htmlContent.replace(originalTbodyContent, `<tbody>${tableRowsHtml}</tbody>`);
            htmlContent = htmlContent.replace('dmd_total', totalDemandQty.toFixed(2));
            htmlContent = htmlContent.replace('supply_qty', totalSupplyQty.toFixed(2));
            updateProgress(100);
            setTimeout(() => notification.destroy(key), 1000);
            const printWindow = window.open('', '_blank');
            if (printWindow) {
                printWindow.document.write(htmlContent);
                printWindow.document.close();
                printWindow.focus();
                printWindow.onload = () => {

                    setTimeout(() => {
                        const contentHeight = printWindow.document.body.scrollHeight;
                        const printablePageHeight = 1022.24;
                        const totalPages = Math.ceil(contentHeight / printablePageHeight);
                        const pageElement = printWindow.document.getElementById('reportTotalPages');
                        if (pageElement) {
                            pageElement.textContent = `1 page of ${totalPages}`;
                        }
                        printWindow.print();
                    }, 500);
                };
            } else {
                notification.error({ message: "Gagal membuka jendela cetak. Pastikan pop-up diizinkan." });
            }
        } catch (error) {
            console.error("Gagal mencetak Picking List:", error);
            notification.error({ message: "Gagal mencetak Picking List. Terjadi kesalahan saat mengambil data atau template." });
        } finally {
            setIsPrinting(false);
            notification.destroy(key);
        }
    };


    return {
        rowSelection,
        selectedRowKeys,
        columns: columns(selectedWarehouseGroupCode),
        form,
        isSoModalVisible,
        showSoModal,
        handleSoModalOk,
        handleSoModalCancel,
        handleSoRecordSelected,
        pickingListItems,
        isAddItemModalVisible,
        showAddItemModal,
        handleAddItemModalOk,
        handleAddItemModalCancel,
        selectedSalesOrderNo,
        selectedWarehouseGroupCode,
        debouncedAutoSave,
        handleDeleteSelectedItems,
        onSelectAvailableItems: handleSelectAvailableItems,
        handleVoid,
        handleSubmit,
        isReadyStatus,
        isStatusDraft,
        isAddEditPickingItemFormModalVisible,
        showAddEditPickingItemFormModal,
        handleAddEditPickingItemFormModalOk,
        handleAddEditPickingItemFormModalCancel,
        editingPickingItem,
        handleRowDoubleClick,
        handlePrint,
        isPrinting,
        isSubmitting,
        isVoiding,
        autosaveStatus,
        pickingStatusMap,
        handleNew: resetToNewForm
    };
};

export const withPickingListLogic = (WrappedComponent: React.ComponentType<any>) => {
    return () => {
        const {
            rowSelection,
            columns,
            form,
            isSoModalVisible,
            showSoModal,
            handleSoModalOk,
            handleSoModalCancel,
            handleSoRecordSelected,
            pickingListItems,
            isAddItemModalVisible,
            showAddItemModal,
            handleAddItemModalOk,
            handleAddItemModalCancel,
            selectedSalesOrderNo,
            selectedWarehouseGroupCode,
            debouncedAutoSave,
            handleDeleteSelectedItems,
            onSelectAvailableItems,
            handleVoid,
            handleSubmit,
            isReadyStatus,
            isStatusDraft,
            isAddEditPickingItemFormModalVisible,
            showAddEditPickingItemFormModal,
            handleAddEditPickingItemFormModalOk,
            handleAddEditPickingItemFormModalCancel,
            editingPickingItem,
            handleRowDoubleClick,
            handlePrint,
            isPrinting,
            isSubmitting,
            isVoiding,
            autosaveStatus,
            pickingStatusMap,
            handleNew
        } = usePickingListLogic();
        return <WrappedComponent
            rowSelection={rowSelection}
            columns={columns}
            form={form}
            isSoModalVisible={isSoModalVisible}
            showSoModal={showSoModal}
            handleSoModalOk={handleSoModalOk}
            handleSoModalCancel={handleSoModalCancel}
            handleSoRecordSelected={handleSoRecordSelected}
            pickingListItems={pickingListItems}
            isAddItemModalVisible={isAddItemModalVisible}
            showAddItemModal={showAddItemModal}
            handleAddItemModalOk={handleAddItemModalOk}
            handleAddItemModalCancel={handleAddItemModalCancel}
            selectedSalesOrderNo={selectedSalesOrderNo}
            selectedWarehouseGroupCode={selectedWarehouseGroupCode}
            debouncedAutoSave={debouncedAutoSave}
            handleDeleteSelectedItems={handleDeleteSelectedItems}
            onSelectAvailableItems={onSelectAvailableItems}
            handleVoid={handleVoid}
            handleSubmit={handleSubmit}
            isReadyStatus={isReadyStatus}
            isStatusDraft={isStatusDraft}
            isAddEditPickingItemFormModalVisible={isAddEditPickingItemFormModalVisible}
            showAddEditPickingItemFormModal={showAddEditPickingItemFormModal}
            handleAddEditPickingItemFormModalOk={handleAddEditPickingItemFormModalOk}
            handleAddEditPickingItemFormModalCancel={handleAddEditPickingItemFormModalCancel}
            editingPickingItem={editingPickingItem}
            handleRowDoubleClick={handleRowDoubleClick}
            handlePrint={handlePrint}
            isSubmitting={isSubmitting}
            isVoiding={isVoiding}
            isPrinting={isPrinting}
            autosaveStatus={autosaveStatus}
            pickingStatusMap={pickingStatusMap}
            handleNew={handleNew}
        />;
    };
};